import { config } from '../config';
import { FormFieldType } from '../typings';
import type {
  AddressTarget,
  ArrayFormField,
  FieldsGroupAddressConfig,
  FieldsGroupFormField,
  FormField,
  FormInitialValues,
} from '../typings';
import { forEachFormField } from './formFieldChildKeys';

/** An address answer as the API takes it: `{ target, key?, entryID? }`. */
export type AddressLink = {
  target: AddressTarget;
  key?: string | null;
  entryID?: string | null;
};

/** A group in the form that carries an address config. */
export type AddressGroupField = (FieldsGroupFormField | ArrayFormField) & {
  address: FieldsGroupAddressConfig;
};

const TARGETS: readonly string[] = ['Own', 'Parent', 'Group'];
const SEPARATOR = ':';

/** The Formik value standing for an address answer, e.g. "Own" or "Group:home". */
export const encodeAddressLink = ({
  target,
  key,
  entryID,
}: AddressLink): string =>
  [target, key ?? '', entryID ?? ''].join(SEPARATOR).replace(/:+$/, '');

/** The address answer a Formik value stands for; undefined while unanswered. */
export const decodeAddressLink = (value: unknown): AddressLink | undefined => {
  if (typeof value !== 'string' || value === '') return undefined;

  const [target, key, entryID] = value.split(SEPARATOR);
  if (!TARGETS.includes(target)) return undefined;

  return {
    target: target as AddressTarget,
    ...(key ? { key } : {}),
    ...(entryID ? { entryID } : {}),
  };
};

export const addressLinkFieldId = (groupFieldId: string): string =>
  `${groupFieldId}${config.addressLinkFieldSuffix}`;

export const addressFieldsShownFieldId = (groupFieldId: string): string =>
  `${groupFieldId}${config.addressFieldsShownFieldSuffix}`;

export type AddressChoiceState = {
  showsChoice: boolean;
  /** The option in effect: the answer, or Own while an optional answer is blank. */
  selectedValue?: string;
  isLinked: boolean;
  /** A required answer is still blank, so every field waits for it. */
  hidesAllFields: boolean;
  hiddenFieldIds: ReadonlySet<string>;
};

const NOTHING_HIDDEN: ReadonlySet<string> = new Set();

/** What a group's address choice shows and hides for a given Formik answer. */
export const getAddressChoiceState = (
  address: FieldsGroupAddressConfig | undefined,
  rawValue: unknown,
): AddressChoiceState => {
  if (!address || address.options.length === 0) {
    return {
      showsChoice: false,
      isLinked: false,
      hidesAllFields: false,
      hiddenFieldIds: NOTHING_HIDDEN,
    };
  }

  // The API stores no link for an optional answer left alone, which means Own.
  const selectedValue =
    typeof rawValue === 'string' && rawValue !== ''
      ? rawValue
      : address.answerRequired
        ? undefined
        : address.options.find(option => option.target === 'Own')?.value;

  const isLinked =
    selectedValue !== undefined &&
    decodeAddressLink(selectedValue)?.target !== 'Own';

  return {
    showsChoice: true,
    selectedValue,
    isLinked,
    hidesAllFields: selectedValue === undefined,
    hiddenFieldIds: isLinked ? new Set(address.hiddenFieldIds) : NOTHING_HIDDEN,
  };
};

const isGroupType = (field: FormField): boolean =>
  field.type === FormFieldType.FieldsGroup ||
  field.type === FormFieldType.Array;

/** Every group in the form that carries an address config, by field id. */
export const indexAddressGroups = (
  formFieldGroups: readonly FormField[],
): Map<string, AddressGroupField> => {
  const index = new Map<string, AddressGroupField>();

  forEachFormField(formFieldGroups, field => {
    if (isGroupType(field) && (field as AddressGroupField).address) {
      index.set(field.fieldId, field as AddressGroupField);
    }
  });

  return index;
};

export const findFormField = (
  fields: readonly FormField[],
  fieldId: string,
): FormField | undefined => {
  let found: FormField | undefined;

  forEachFormField(fields, field => {
    if (!found && field.fieldId === fieldId) found = field;
  });

  return found;
};

/** A target counts only while it is not marked Not Applicable. */
export const isAddressGroupLive = (
  group: AddressGroupField,
  values: FormInitialValues,
): boolean =>
  !group.notApplicableField ||
  values[group.notApplicableField.fieldId] !== true;

export type AddressOwner =
  | { kind: 'group'; group: AddressGroupField }
  /** The chain leaves this form; the summary is the server's. */
  | { kind: 'server'; summary?: string }
  /** Unanswered, not live, or a loop. */
  | { kind: 'none' };

/** Follows answered links from a group to the one that holds the address. */
export const resolveAddressOwner = (
  groupFieldId: string,
  index: ReadonlyMap<string, AddressGroupField>,
  values: FormInitialValues,
): AddressOwner => {
  // Concurrent saves can still store a loop, so a revisit counts as unanswered.
  const visited = new Set<string>();
  let current = index.get(groupFieldId);

  while (current && !visited.has(current.fieldId)) {
    visited.add(current.fieldId);

    if (!isAddressGroupLive(current, values)) return { kind: 'none' };

    const { showsChoice, selectedValue, isLinked } = getAddressChoiceState(
      current.address,
      values[addressLinkFieldId(current.fieldId)],
    );

    if (!showsChoice || (selectedValue !== undefined && !isLinked)) {
      return { kind: 'group', group: current };
    }

    if (selectedValue === undefined) return { kind: 'none' };

    const option = current.address.options.find(
      o => o.value === selectedValue,
    );
    const next = option?.targetFieldId
      ? index.get(option.targetFieldId)
      : undefined;

    if (!next) {
      // effectiveAddress follows only the saved answer; the chosen option's
      // preview also follows a choice made since the section loaded.
      return {
        kind: 'server',
        summary: option ? option.preview : current.address.effectiveAddress,
      };
    }

    current = next;
  }

  return { kind: 'none' };
};
